import { ExpandMore, ExitToApp, PersonOutline, SettingsOutlined } from '@mui/icons-material';
import { Navbar as FinaraNavbar, type NavItem } from 'finara-components';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationDropdown } from '@/components/notifications';
import { useAuth } from '@/hooks/useAuth';

interface NavbarProps {
  navItems: NavItem[];
  userName?: string;
  activeItem?: string;
  pageTitle?: string;
  section?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  navItems,
  userName = 'Admin',
  activeItem,
  pageTitle,
  section,
}) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const displayName = user?.name ?? userName;
  const userInitials = displayName
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    void logout();
  };

  const handleNavigate = (path: string) => {
    setIsDropdownOpen(false);
    void navigate(path);
  };

  // Right side navbar content
  const rightContent = (
    <div className="flex items-center space-x-3">
      {/* Search Bar */}
      <div className="hidden lg:block">
        <input
          type="text"
          placeholder="Buscar empleados..."
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <NotificationDropdown />

      {/* User Profile Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center space-x-2 cursor-pointer px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-navbar-text hover:text-navbar-hover"
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #A6E77F 0%, #175757 100%)' }}>
              <span className="text-white text-[10.5px] font-medium select-none">{userInitials.slice(0, 2)}</span>
            </div>
            <div className="flex flex-col justify-start min-w-0">
              <span className="text-[14px] font-semibold text-navbar-text leading-tight truncate">{displayName}</span>
              <span className="text-[12.25px] text-muted leading-tight truncate">Administrador</span>
            </div>
          </div>
          <ExpandMore fontSize="small" />
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-surface rounded-lg shadow-lg border border-border py-2 z-50">
            <button
              onClick={() => handleNavigate('/perfil')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-white hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
            >
              <PersonOutline fontSize="small" className="text-muted" />
              Perfil
            </button>
            <button
              onClick={() => handleNavigate('/configuracion')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-white hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
            >
              <SettingsOutlined fontSize="small" className="text-muted" />
              Configuración
            </button>
            <div className="border-t border-border my-1"></div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 cursor-pointer"
            >
              <ExitToApp fontSize="small" />
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (activeItem) {
    navItems = navItems.map(item => ({
      ...item,
      active:
        'text' in item
          ? item.text === activeItem && (item.active = true)
          : item.title === activeItem && (item.active = true),
    }));
  }

  const navItemsWithLogout = [
    ...navItems,
    { text: 'Cerrar Sesión', url: '#logout', icon: <ExitToApp fontSize="small" /> },
  ];

  return (
    <>
      <FinaraNavbar
        logoAlt="Finara"
        logoUrl="/dashboard-empresarial"
        navItems={navItemsWithLogout}
        rightContent={rightContent}
        showBorder={false}
        onNavLinkClick={item => {
          if (item.url === '#logout') handleLogout();
        }}
      />
      {pageTitle && (
        <div className="border-b border-navbar-border">
          <div className="py-2 px-4">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-brand-blue">Finara</span>
                {section && (
                  <>
                    <span className="text-brand-white">/</span>
                    <span className="text-gray-500">{section}</span>
                  </>
                )}
                {pageTitle && (
                  <>
                    <span className="text-brand-white">/</span>
                    <span className="font-medium text-brand-white">{pageTitle}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
